import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Music2, RefreshCw, ChevronDown } from 'lucide-react';
import LyricsService from '../../services/LyricsService';
import { getPrefetchedLyrics } from '../../services/LyricsPrefetcher';

/**
 * In-App Lyrics Viewer Component
 * Apple Music-inspired design with smooth scrolling
 */
const LyricsViewer = ({ 
  songId, 
  songTitle, 
  artistName,
  artworkUrl,
  duration,
  musicKit, 
  currentPlaybackTime = 0,
  isPlaying = false,
  onSeek,
  onClose,
  isDarkMode = true 
}) => {
  const [lyrics, setLyrics] = useState(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const scrollContainerRef = useRef(null);
  const lineRefs = useRef({});
  const lyricsServiceRef = useRef(new LyricsService());
  const isScrollingRef = useRef(false);

  // INSTANT: Check prefetched cache immediately on mount
  useEffect(() => {
    if (!songTitle || !artistName) return;
    
    // Check prefetch cache first
    const prefetched = getPrefetchedLyrics(songTitle, artistName);
    if (prefetched) {
      console.log('[LyricsViewer] Using prefetched lyrics (INSTANT)');
      setLyrics(prefetched);
      setIsLoading(false);
      setError(null);
      return;
    }
    
    // No cache hit, load immediately
    console.log('[LyricsViewer] No prefetch cache, loading lyrics...');
    loadLyrics();
  }, [songTitle, artistName]);

  // Scroll to line function - must be defined before useEffect that uses it
  const scrollToLine = useCallback((index) => {
    const lineElement = lineRefs.current[index];
    const container = scrollContainerRef.current;
    
    if (lineElement && container) {
      const containerHeight = container.clientHeight;
      const lineTop = lineElement.offsetTop;
      const lineHeight = lineElement.clientHeight;
      
      // Center the line in view with padding
      const targetScrollTop = lineTop - (containerHeight / 2) + (lineHeight / 2);
      
      // Use native smooth scrolling with a controlled duration
      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }
  }, []);

  // Smooth sync lyrics with playback time - optimized for consistent scrolling
  useEffect(() => {
    if (!lyrics || !lyrics.lines || lyrics.lines.length === 0) return;
    
    const time = currentPlaybackTime || 0;
    
    if (lyrics.type === 'synced') {
      let newIndex = 0;
      
      for (let i = 0; i < lyrics.lines.length; i++) {
        const line = lyrics.lines[i];
        if (line.time !== null && line.time <= time) {
          newIndex = i;
        } else if (line.time !== null && line.time > time) {
          break;
        }
      }
      
      // Always scroll to current line, even if index hasn't changed
      // This ensures lyrics stay synced during playback
      if (newIndex !== currentLineIndex) {
        console.log('[LyricsViewer] Time:', time.toFixed(2), 'Line:', newIndex, 'Text:', lyrics.lines[newIndex]?.text?.substring(0, 30));
        setCurrentLineIndex(newIndex);
        scrollToLine(newIndex);
      }
    }
  }, [currentPlaybackTime, lyrics, currentLineIndex, scrollToLine]);

  const loadLyrics = async (isRetry = false) => {
    if (!songTitle || !artistName) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const lyricsData = await lyricsServiceRef.current.fetchLyrics(songTitle, artistName, duration);
      setLyrics(lyricsData);
      setCurrentLineIndex(0);
      
    } catch (err) {
      console.error('[LyricsViewer] Load lyrics error:', err);
      setError(err.message || 'Unable to load lyrics');
      
      // Auto-retry once on first load failure (not on manual retry)
      if (!isRetry && !err.message?.includes('not found')) {
        console.log('[LyricsViewer] Auto-retrying lyrics load...');
        setTimeout(() => loadLyrics(true), 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLineClick = (line, index) => {
    if (line.time !== null && onSeek) {
      onSeek(line.time);
      setCurrentLineIndex(index);
    }
  };

  // Theme colors - darker text for light mode readability
  const bgColor = isDarkMode ? '#0a0a0a' : '#f5f5f5';
  const textColor = isDarkMode ? 'text-white' : 'text-gray-900';
  const mutedColor = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const pastLineColor = isDarkMode ? 'text-gray-600' : 'text-gray-500';
  const futureLineColor = isDarkMode ? 'text-gray-400' : 'text-gray-700';
  const currentLineColor = isDarkMode ? 'text-purple-400' : 'text-purple-700';

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: bgColor }}
    >
      {/* Blurred album art background - VERY VISIBLE */}
      {artworkUrl && (
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${artworkUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(50px) brightness(0.7)',
            transform: 'scale(1.3)',
            opacity: isDarkMode ? 0.85 : 0.7,
          }}
        />
      )}
      
      {/* Subtle gradient overlay - more transparent to show album */}
      <div 
        className="absolute inset-0" 
        style={{
          background: isDarkMode 
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.3) 100%)'
            : 'linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.4) 100%)'
        }}
      />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 pt-12">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {artworkUrl ? (
            <img 
              src={artworkUrl} 
              alt={songTitle}
              className="w-12 h-12 rounded-lg shadow-lg flex-shrink-0"
            />
          ) : (
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
              <Music2 className={`w-6 h-6 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>
          )}
          
          <div className="min-w-0 flex-1">
            <h2 className={`font-semibold text-sm truncate ${textColor}`}>
              {songTitle || 'Unknown Song'}
            </h2>
            <p className={`text-xs truncate ${mutedColor}`}>
              {artistName || 'Unknown Artist'}
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className={`ml-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
            isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-black/10 hover:bg-black/20'
          }`}
          data-testid="close-lyrics-btn"
        >
          <ChevronDown className={`w-5 h-5 ${textColor}`} />
        </button>
      </div>

      {/* Lyrics Content with smooth scrolling - optimized for mobile */}
      <div 
        ref={scrollContainerRef}
        className="relative z-10 flex-1 overflow-y-auto px-6 overscroll-none"
        style={{ 
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'y proximity',
        }}
      >
        {/* Loading - Show song info while loading */}
        {isLoading && (
          <div className="h-full flex flex-col items-center justify-center gap-4">
            <div className={`w-10 h-10 border-2 rounded-full animate-spin ${
              isDarkMode ? 'border-white/20 border-t-white' : 'border-gray-300 border-t-gray-600'
            }`} />
            <p className={`text-sm ${mutedColor}`}>Loading lyrics...</p>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="h-full flex flex-col items-center justify-center gap-4 px-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
              <Music2 className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
            <div className="text-center">
              <p className={`font-medium mb-1 ${textColor}`}>Lyrics Not Available</p>
              <p className={`text-sm ${mutedColor}`}>{error}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadLyrics}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                  isDarkMode ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-black/10 text-gray-900 hover:bg-black/20'
                }`}
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
              <button
                onClick={() => {
                  const query = encodeURIComponent(`${songTitle} ${artistName} lyrics`);
                  window.open(`https://www.google.com/search?q=${query}`, '_blank');
                }}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  isDarkMode ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-black/10 text-gray-900 hover:bg-black/20'
                }`}
              >
                Search Online
              </button>
            </div>
          </div>
        )}

        {/* Lyrics display with ultra-smooth transitions */}
        {lyrics && !isLoading && !error && (
          <>
            <div className="h-40" />

            {lyrics.lines.map((line, index) => {
              const isCurrent = index === currentLineIndex;
              const isPast = index < currentLineIndex;
              const isFuture = index > currentLineIndex;
              const isClickable = line.time !== null;
              
              return (
                <div
                  key={line.id}
                  ref={el => lineRefs.current[index] = el}
                  onClick={() => handleLineClick(line, index)}
                  className={`py-3 px-4 rounded-lg mb-2 text-center transition-all ${
                    isClickable 
                      ? 'cursor-pointer active:scale-95 active:bg-white/10 hover:bg-white/5 touch-manipulation' 
                      : ''
                  }`}
                  style={{
                    transition: 'transform 0.5s ease-out, opacity 0.5s ease-out, background-color 0.15s ease-out',
                    transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
                    opacity: isCurrent ? 1 : isPast ? 0.35 : 0.65,
                    WebkitTapHighlightColor: 'rgba(168, 85, 247, 0.3)',
                  }}
                >
                  <p
                    className={`leading-relaxed font-medium ${
                      isCurrent 
                        ? `text-2xl font-bold ${currentLineColor}` 
                        : isPast 
                          ? `text-lg ${pastLineColor}`
                          : `text-lg ${futureLineColor}`
                    }`}
                    style={{
                      transition: 'all 0.5s ease-out',
                      textShadow: isCurrent 
                        ? (isDarkMode 
                            ? '0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(168, 85, 247, 0.4)' 
                            : '0 0 15px rgba(147, 51, 234, 0.4)')
                        : 'none',
                    }}
                  >
                    {line.text}
                  </p>
                </div>
              );
            })}

            <div className="h-48" />
          </>
        )}
      </div>

    </div>
  );
};

export default LyricsViewer;
